import {createStyles} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';
import {Node, ReactFlow, ReactFlowProvider} from 'reactflow';

import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls, {MIN_ZOOM, MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';

import ProcedureRunDesignFlowchartColumnNode, {
  PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE,
  ProcedureRunDesignFlowchartColumnNodeData,
  createProcedureRunDesignFlowchartColumnNode,
  FALLBACK_COLUMN_NODE_WIDTH,
  FALLBACK_COLUMN_NODE_HEIGHT,
} from './ProcedureRunDesignFlowchartColumnNode';
import ProcedureRunDesignFlowchartNode, {
  PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
  ProcedureRunDesignFlowchartNodeData,
  createProcedureRunDesignFlowchartNode,
  FALLBACK_NODE_WIDTH,
  FALLBACK_NODE_HEIGHT,
} from './ProcedureRunDesignFlowchartNode';

const HORIZONTAL_GAP = 75;
const VERTICAL_GAP = 32;
const NODE_START_X = 24;
const NODE_START_Y = 24;
const COLUMN_HEADER_BOTTOM_GAP = 20;

const NODE_TYPES = {
  [PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE]: ProcedureRunDesignFlowchartNode,
  [PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE]: ProcedureRunDesignFlowchartColumnNode,
};

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 0, y: 0, zoom: 1},
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
  proOptions: REACT_FLOW_PRO_OPTIONS,
};

interface ProcedureRunDesignFlowchartProps {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  controlsShiftLeft?: number;
  onNodeClick?: () => void;
}

function makeNodes(
  conditions: Array<BaseCondition>,
  methods: Array<ExperimentMethod>
): Array<Node<ProcedureRunDesignFlowchartNodeData | ProcedureRunDesignFlowchartColumnNodeData>> {
  const gridRowStartY = NODE_START_Y + FALLBACK_COLUMN_NODE_HEIGHT + COLUMN_HEADER_BOTTOM_GAP;
  const nodes: Array<Node<ProcedureRunDesignFlowchartNodeData | ProcedureRunDesignFlowchartColumnNodeData>> =
    [];

  methods.forEach((method, methodIndex) => {
    const columnNode = createProcedureRunDesignFlowchartColumnNode({
      methodId: method.id,
      methodName: method.name,
      factorsSet: 0, // TODO(BNCH-153703): Add factors set
      shouldHighlight: false, // TODO(BNCH-153697): Add should highlight based on selected/hovered condition
    });
    nodes.push({
      ...columnNode,
      position: {
        x: NODE_START_X + methodIndex * (FALLBACK_COLUMN_NODE_WIDTH + HORIZONTAL_GAP),
        y: NODE_START_Y,
      },
    });
  });

  // Grid nodes (one per condition × replicate × method)
  let currentRowIndex = 0;
  conditions.forEach((condition, conditionIndex) => {
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      for (let methodIndex = 0; methodIndex < methods.length; methodIndex++) {
        const method = methods[methodIndex];
        const node = createProcedureRunDesignFlowchartNode({
          conditionIndex,
          replicateIndex,
          methodId: method.id,
          conditionName: condition.name,
          replicateNumber: replicateIndex + 1,
          conditionDescription: condition.description,
        });
        nodes.push({
          ...node,
          position: {
            x: NODE_START_X + methodIndex * (FALLBACK_NODE_WIDTH + HORIZONTAL_GAP),
            y: gridRowStartY + currentRowIndex * (FALLBACK_NODE_HEIGHT + VERTICAL_GAP),
          },
        });
      }
      currentRowIndex += 1;
    }
  });

  return nodes;
}

export default function ProcedureRunDesignFlowchart(props: ProcedureRunDesignFlowchartProps): JSX.Element {
  const {classes} = useStyles();
  const {conditions, methods, controlsShiftLeft, onNodeClick} = props;
  const nodes = useMemo(() => makeNodes(conditions, methods), [conditions, methods]);
  const handleNodeClick = useCallback(() => {
    onNodeClick?.();
  }, [onNodeClick]);

  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <div
          className={classes.flowchartContainer}
          {...getTestProps({component: 'ProcedureRunDesignFlowchart', element: 'root'})}
        >
          <ReactFlow
            nodes={nodes}
            // TODO (BNCH-156156): Add edges
            edges={[]}
            nodeTypes={NODE_TYPES}
            {...STATIC_PROPS}
            onNodeClick={handleNodeClick}
          >
            <HorizontalControls shiftLeft={controlsShiftLeft ?? 0} />
            <DefaultBackground />
          </ReactFlow>
          {/* TODO (BNCH-153423): Add empty state for when there are no nodes */}
        </div>
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

const useStyles = createStyles(() => ({
  flowchartContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
}));
